import { useCallback, useEffect, useRef, useState } from 'react';
import {
  effortIsActive,
  getProviderForModel,
  getSupportedEffortLevels,
} from '../../../../domain/llm';
import type { LlmModelId, LlmStreamDoc } from '../../../../domain/llm';
import { firebaseLlmGateway } from '../../../../platform/firebase/llmGateway';
import { logGlobalError } from '../../../../utils/globalErrorLogger';

export interface LlmTestHarnessViewModel {
  busy: boolean;
  // Stream configuration
  prompt: string;
  setPrompt: (v: string) => void;
  model: LlmModelId;
  setModel: (v: LlmModelId) => void;
  thinkingEnabled: boolean;
  setThinkingEnabled: (v: boolean) => void;
  thinkingBudget: number;
  setThinkingBudget: (v: number) => void;
  effort: string;
  setEffort: (v: string) => void;
  webSearchEnabled: boolean;
  setWebSearchEnabled: (v: boolean) => void;
  // Live stream state
  streamDocId: string | null;
  streamDoc: LlmStreamDoc | null;
  sending: boolean;
  lastError: string | null;
  // Actions
  initialize: () => void;
  send: () => Promise<void>;
  reset: () => void;
}

// Ordered current-generation first by provider, then immediate predecessors.
const ALL_MODELS: LlmModelId[] = [
  // Anthropic — current
  'claude-opus-5',
  'claude-opus-4-7',
  'claude-sonnet-4-6',
  'claude-haiku-4-5',
  // Anthropic — predecessors
  'claude-opus-4-6',
  'claude-sonnet-4-5',
  // OpenAI — current (GPT-5.6 Sol / GPT-5.5)
  'gpt-5.6-sol',
  'gpt-5.5',
  'gpt-5.5-pro',
  // OpenAI — current (GPT-5.4)
  'gpt-5.4',
  'gpt-5.4-pro',
  'gpt-5.4-mini',
  'gpt-5.4-nano',
  // OpenAI — predecessor
  'gpt-5',
  'gpt-5-mini',
  'gpt-5-nano',
  // Gemini — current (3.x)
  'gemini-3.7-flash',
  'gemini-3.1-pro-preview',
  'gemini-3-flash-preview',
  'gemini-3.1-flash-lite',
  // Gemini — predecessor (deprecates 2026-06-17)
  'gemini-2.5-pro',
  // Together
  'moonshotai/Kimi-K3',
  'zai-org/GLM-5.2',
  'MiniMaxAI/MiniMax-M2.7',
];

// Models where the UI shows an explicit thinking on/off toggle.
// `optional` thinkingMode only — `adaptive` (Opus 4.7), `always` (Gemini 3.x),
// and `none` (Together) shouldn't show a toggle.
const EXTENDED_THINKING_MODELS: ReadonlySet<LlmModelId> = new Set<LlmModelId>([
  'claude-sonnet-4-6',
  'claude-haiku-4-5',
  'claude-opus-4-6',
  'claude-sonnet-4-5',
  'gpt-5.6-sol',
  'gpt-5.5',
  'gpt-5.5-pro',
  'gpt-5.4',
  'gpt-5.4-pro',
  'gpt-5.4-mini',
  'gpt-5.4-nano',
  'gpt-5',
  'gpt-5-mini',
  'gpt-5-nano',
  // Gemini 2.5 Pro (predecessor) — uses the legacy budget_tokens path with
  // an opt-in toggle. Gemini 3.x is always-on and has no toggle.
  'gemini-2.5-pro',
]);

export function supportsExtendedThinking(model: LlmModelId): boolean {
  return EXTENDED_THINKING_MODELS.has(model);
}

// Web search support by provider. Anthropic uses date-suffixed tool ids;
// OpenAI uses the bare `web_search` type on the Responses API; Gemini uses
// hosted Google Search grounding (`googleSearch`). Together has no hosted
// tools today.
export function supportsWebSearch(model: LlmModelId): boolean {
  const provider = getProviderForModel(model);
  return provider === 'anthropic' || provider === 'openai' || provider === 'gemini';
}

// Per-provider web_search tool spec. All three adapters accept the cross-
// provider `{ type: 'web_search' }` form, but sending the canonical shape
// per provider keeps server-side logs clean (no "alias accepted" noise).
const WEB_SEARCH_TOOL_BY_PROVIDER = {
  anthropic: { type: 'web_search_20250305', name: 'web_search' },
  openai:    { type: 'web_search' },
  gemini:    { type: 'googleSearch' },
} as const;

export function useLlmTestHarnessViewModel(): LlmTestHarnessViewModel {
  const [busy, setBusy] = useState<boolean>(true);
  const [prompt, setPrompt] = useState<string>('');
  const [model, setModel] = useState<LlmModelId>('claude-haiku-4-5');
  const [thinkingEnabled, setThinkingEnabled] = useState<boolean>(false);
  const [thinkingBudget, setThinkingBudget] = useState<number>(4000);
  // Effort default: 'medium' is the safe middle for every supported
  // vocabulary (Anthropic low/medium/high, OpenAI minimal/low/medium/high/xhigh,
  // Gemini minimal/low/medium/high/xhigh). Reset on model switch if the
  // current value isn't valid for the new model.
  const [effort, setEffort] = useState<string>('medium');
  const [webSearchEnabled, setWebSearchEnabled] = useState<boolean>(false);
  const [streamDocId, setStreamDocId] = useState<string | null>(null);
  const [streamDoc, setStreamDoc] = useState<LlmStreamDoc | null>(null);
  const [sending, setSending] = useState<boolean>(false);
  const [lastError, setLastError] = useState<string | null>(null);

  const unsubRef = useRef<(() => void) | null>(null);

  // Force the thinking toggle off whenever the selected model doesn't accept
  // the extended-thinking parameter (Opus 4.7 uses adaptive thinking;
  // MiniMax has no extended-thinking concept; Gemini 3.x, GLM, and Kimi are
  // always-on).
  useEffect(() => {
    if (!supportsExtendedThinking(model) && thinkingEnabled) {
      setThinkingEnabled(false);
    }
  }, [model, thinkingEnabled]);

  // Same for web search — silently clear on models that don't support it.
  useEffect(() => {
    if (!supportsWebSearch(model) && webSearchEnabled) {
      setWebSearchEnabled(false);
    }
  }, [model, webSearchEnabled]);

  // Reset effort to 'medium' when switching to a model whose vocabulary
  // doesn't include the current value. (Common case: leaving GPT-5.4 at
  // 'xhigh' then switching to Anthropic, which caps at 'high', or Together
  // reasoning models, whose vocabularies vary.)
  useEffect(() => {
    const allowed = getSupportedEffortLevels(model);
    if (allowed && !allowed.includes(effort)) {
      setEffort(allowed.includes('medium') ? 'medium' : allowed[0]);
    }
  }, [model, effort]);

  const initialize = useCallback(() => {
    setBusy(true);
    setBusy(false);
  }, []);

  const reset = useCallback(() => {
    if (unsubRef.current) {
      unsubRef.current();
      unsubRef.current = null;
    }
    setStreamDocId(null);
    setStreamDoc(null);
    setLastError(null);
  }, []);

  const send = useCallback(async () => {
    if (sending || !prompt.trim()) return;
    setLastError(null);

    // Tear down any prior subscription before creating a new stream.
    if (unsubRef.current) {
      unsubRef.current();
      unsubRef.current = null;
    }

    const id = await firebaseLlmGateway.createStreamDocId();
    setStreamDocId(id);
    setStreamDoc(null);

    // Subscribe BEFORE firing the callable so the first thinking deltas aren't
    // missed. The Firestore rules permit reading a non-existent llm_streams
    // doc (see firestore.rules), so the listener stays alive during the
    // brief window between subscribe and handler-creates-doc.
    unsubRef.current = await firebaseLlmGateway.subscribeToStream(id, setStreamDoc);

    // Pick the per-provider web_search spec when the user enabled it.
    // `supportsWebSearch` already guards against the Together case, but we
    // defensively check the provider here so TS knows the lookup is safe.
    let tools: Array<Record<string, unknown>> | null = null;
    if (webSearchEnabled) {
      const provider = getProviderForModel(model);
      if (provider === 'anthropic' || provider === 'openai' || provider === 'gemini') {
        tools = [WEB_SEARCH_TOOL_BY_PROVIDER[provider]];
      }
    }

    setSending(true);
    try {
      // Send `effort` only when it'll actually reach the wire (Gemini 3.x / GLM
      // always; optional-thinking models only when thinking is on). When
      // thinking is off on Anthropic/OpenAI, the adapter ignores effort
      // anyway — sending it would be confusing log noise.
      const effortToSend = effortIsActive(model, thinkingEnabled) ? effort : null;
      await firebaseLlmGateway.startStream({
        streamDocId: id,
        message: prompt,
        model,
        thinking: thinkingEnabled
          ? { type: 'enabled', budget_tokens: thinkingBudget }
          : null,
        effort: effortToSend,
        tools,
      });
    } catch (err) {
      // chatLlmStream threw synchronously (e.g. callable invalid-argument) —
      // no stream doc will ever exist, so tear down the subscription we
      // attached above. Without this, the listener leaks until unmount.
      if (unsubRef.current) {
        unsubRef.current();
        unsubRef.current = null;
      }
      const message = err instanceof Error ? err.message : String(err);
      setLastError(message);
      logGlobalError(err, {
        context: 'LlmTestTool.chatLlmStream',
        metadata: { model, streamDocId: id },
      });
    } finally {
      setSending(false);
    }
  }, [sending, prompt, model, thinkingEnabled, thinkingBudget, effort, webSearchEnabled]);

  useEffect(() => {
    return () => {
      if (unsubRef.current) unsubRef.current();
    };
  }, []);

  return {
    busy,
    prompt,
    setPrompt,
    model,
    setModel,
    thinkingEnabled,
    setThinkingEnabled,
    thinkingBudget,
    setThinkingBudget,
    effort,
    setEffort,
    webSearchEnabled,
    setWebSearchEnabled,
    streamDocId,
    streamDoc,
    sending,
    lastError,
    initialize,
    send,
    reset,
  };
}

export { ALL_MODELS };
